import type { Shipment } from '@/lib/services/supabase/shipments';
import type { ShipmentItem } from '@/types/shipment-items';

export type ShipmentMergeStrategy = 'deduplicate' | 'combine';

export interface ShipmentMergeCandidate {
  id: string;
  canonicalShipment: Shipment;
  duplicateShipment: Shipment;
  strategy: ShipmentMergeStrategy;
  sharedItems: ShipmentItem[];
  uniqueDuplicateItems: ShipmentItem[];
  matchingTrackingNumbers: string[];
}

function isMergeEligibleShipment(shipment: Shipment): boolean {
  return !shipment.records_delivery_only && shipment.status !== 'cancelled' && shipment.merged_into_shipment_id == null;
}

function getItemIdentity(item: ShipmentItem): string {
  return item.component_id ?? `description:${item.item_description.trim().toLowerCase()}`;
}

function getTrackingNumbers(shipment: Shipment, items: ShipmentItem[]): string[] {
  return [...new Set([
    shipment.tracking_number,
    ...items.map((item) => item.tracking_number),
  ].filter((tracking): tracking is string => Boolean(tracking)))];
}

function hasConflictingDeliveryDetails(canonical: ShipmentItem, duplicate: ShipmentItem): boolean {
  return (
    (canonical.tracking_number !== null
      && duplicate.tracking_number !== null
      && canonical.tracking_number !== duplicate.tracking_number)
    || (canonical.carrier !== null && duplicate.carrier !== null && canonical.carrier !== duplicate.carrier)
    || (canonical.estimated_delivery_date !== null
      && duplicate.estimated_delivery_date !== null
      && canonical.estimated_delivery_date !== duplicate.estimated_delivery_date)
  );
}

function daysBetween(leftDate: string, rightDate: string): number {
  return Math.abs(Date.parse(leftDate) - Date.parse(rightDate)) / 86_400_000;
}

/**
 * Finds only near-term, evidence-compatible candidates. Similar kits shipped
 * weeks apart are legitimate replenishments until a person explicitly reviews them.
 */
export function findShipmentMergeCandidates(
  shipments: Shipment[],
  shipmentItems: ShipmentItem[]
): ShipmentMergeCandidate[] {
  const itemsByShipment = new Map<string, ShipmentItem[]>();
  const activeShipments = shipments.filter(isMergeEligibleShipment);
  const candidates: ShipmentMergeCandidate[] = [];
  const inspectedPairs = new Set<string>();

  for (const item of shipmentItems) {
    const items = itemsByShipment.get(item.shipment_id) ?? [];
    items.push(item);
    itemsByShipment.set(item.shipment_id, items);
  }

  const shipmentsByLocation = new Map<string, Shipment[]>();
  for (const shipment of activeShipments) {
    const locationShipments = shipmentsByLocation.get(shipment.location_id) ?? [];
    locationShipments.push(shipment);
    shipmentsByLocation.set(shipment.location_id, locationShipments);
  }

  for (const locationShipments of shipmentsByLocation.values()) {
    for (let index = 0; index < locationShipments.length; index += 1) {
      for (let comparisonIndex = index + 1; comparisonIndex < locationShipments.length; comparisonIndex += 1) {
        const firstShipment = locationShipments[index]!;
        const secondShipment = locationShipments[comparisonIndex]!;
        const pairId = [firstShipment.id, secondShipment.id].sort().join(':');
        if (inspectedPairs.has(pairId)) continue;
        inspectedPairs.add(pairId);

        const firstItems = itemsByShipment.get(firstShipment.id) ?? [];
        const secondItems = itemsByShipment.get(secondShipment.id) ?? [];
        const firstTracking = getTrackingNumbers(firstShipment, firstItems);
        const secondTracking = getTrackingNumbers(secondShipment, secondItems);
        const matchingTrackingNumbers = firstTracking.filter((tracking) => secondTracking.includes(tracking));

        const canonicalShipment = firstShipment.creation_source === 'purchase_order_fulfillment'
          ? firstShipment
          : secondShipment.creation_source === 'purchase_order_fulfillment'
            ? secondShipment
            : (firstShipment.created_at ?? firstShipment.shipment_date)
              <= (secondShipment.created_at ?? secondShipment.shipment_date)
              ? firstShipment
              : secondShipment;
        const duplicateShipment = canonicalShipment.id === firstShipment.id ? secondShipment : firstShipment;
        const canonicalItems = itemsByShipment.get(canonicalShipment.id) ?? [];
        const duplicateItems = itemsByShipment.get(duplicateShipment.id) ?? [];
        const canonicalItemByIdentity = new Map(canonicalItems.map((item) => [getItemIdentity(item), item]));
        const sharedItems: ShipmentItem[] = [];
        const uniqueDuplicateItems: ShipmentItem[] = [];
        let hasConflict = false;

        for (const duplicateItem of duplicateItems) {
          const canonicalItem = canonicalItemByIdentity.get(getItemIdentity(duplicateItem));
          if (!canonicalItem) {
            uniqueDuplicateItems.push(duplicateItem);
            continue;
          }

          sharedItems.push(duplicateItem);
          if (hasConflictingDeliveryDetails(canonicalItem, duplicateItem)) {
            hasConflict = true;
          }
        }

        if (sharedItems.length === 0 || hasConflict) continue;

        const shipmentDateDistance = daysBetween(canonicalShipment.shipment_date, duplicateShipment.shipment_date);
        const hasManualAndPoPair = canonicalShipment.creation_source === 'purchase_order_fulfillment'
          && duplicateShipment.creation_source === 'manual';

        if (hasManualAndPoPair && shipmentDateDistance <= 2) {
          const hasQuantityMismatch = sharedItems.some((duplicateItem) => {
            const canonicalItem = canonicalItemByIdentity.get(getItemIdentity(duplicateItem));
            return canonicalItem?.quantity !== duplicateItem.quantity;
          });
          if (!hasQuantityMismatch) {
            candidates.push({
              id: pairId,
              canonicalShipment,
              duplicateShipment,
              strategy: 'deduplicate',
              sharedItems,
              uniqueDuplicateItems,
              matchingTrackingNumbers,
            });
          }
          continue;
        }

        const hasSameShipmentEvidence = matchingTrackingNumbers.length > 0 && shipmentDateDistance <= 1;
        if (hasSameShipmentEvidence) {
          candidates.push({
            id: pairId,
            canonicalShipment,
            duplicateShipment,
            strategy: 'combine',
            sharedItems,
            uniqueDuplicateItems,
            matchingTrackingNumbers,
          });
        }
      }
    }
  }

  return candidates;
}
